import React, {useEffect} from "react";
import cn from "classnames";
import PropTypes from "prop-types";
import FormControl from "@material-ui/core/FormControl";
import Input from "@material-ui/core/Input";
import MenuItem from "@material-ui/core/MenuItem";
import withStyles from "components/with-styles";
import MuiSelect from "@material-ui/core/Select";
import Label from "../partial/label";
import FormHelperText from "components/form/form-helper-text";
import FormFieldError from "components/form/form-field-error";
import styles from "./styles/select.css.js";
import useMediaQuery from "@material-ui/core/useMediaQuery";

const renderElements = ({
  elements,
  getValueFromElement,
  getLabelFromElement,
  classes,
  emptyValueText,
  emptyValue,
}) => {
  const emptyElement = emptyValueText ? (
    <MenuItem key={emptyValue} value={emptyValue}>
      <em>{emptyValueText}</em>
    </MenuItem>
  ) : null;

  const selectOptions = elements.map(element => (
    <MenuItem
      classes={{
        root: classes.menuItem,
      }}
      key={getValueFromElement(element)}
      value={getValueFromElement(element)}
    >
      {getLabelFromElement(element)}
    </MenuItem>
  ));

  return emptyValueText ? [emptyElement, ...selectOptions] : selectOptions;
};

const Select = ({
  className,
  classes,
  getValueFromElement = element => element,
  getLabelFromElement = element => element,
  label,
  id,
  name,
  value,
  onChange,
  elements,
  emptyValueText,
  emptyValue = "",
  helperText,
  error = false,
  spaced,
  disabled = false,
  marginDense = false,
  selectClasses = {},
  ...props
}) => {
  const matchesMobile = useMediaQuery("(max-width: 385px)");

  return (
    <FormControl
      disabled={disabled}
      error={error}
      margin={spaced ? "normal" : "none"}
      fullWidth
      className={cn(className, classes.select_formcontrol)}
    >
      <Label label={label} id={id} />

      <MuiSelect
        classes={
          marginDense && matchesMobile ? { select: classes.select_dense, ...selectClasses } : { ...selectClasses }
        }
        value={value || ""}
        onChange={onChange}
        error={error}
        input={<Input name={name} id={id} />}
        {...props}
      >
        {renderElements({ elements, getValueFromElement, getLabelFromElement, classes, emptyValueText, emptyValue })}
      </MuiSelect>

      <FormHelperText text={helperText} error={error} />
      <FormFieldError name={name} />
    </FormControl>
  );
};

Select.propTypes = {
  /**
   * Material UI select classes object
   */
  selectClasses: PropTypes.object,
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Etiqueta opcional para el campo
   */
  label: PropTypes.string,
  /**
   * Función que obtiene el valor de la opción a partir de un elemento
   */
  getValueFromElement: PropTypes.func,
  /**
   * Función que obtiene el valor de la etiqueta a partir de un elemento
   */
  getLabelFromElement: PropTypes.func,
  /**
   * Id del selector
   */
  id: PropTypes.string.isRequired,
  /**
   * Nombre del selector
   */
  name: PropTypes.string.isRequired,
  /**
   * Valor actual del selector
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  /**
   * Label del texto de la opción vacía, si existe (opcional)
   */
  emptyValueText: PropTypes.string,
  /**
   * Valor del texto de la opción vacía, si existe (opcional)
   */
  emptyValue: PropTypes.string,
  /**
   * Texto de ayuda que aparece bajo el select
   */
  helperText: PropTypes.string,
  /**
   * Función que se ejecuta al cambiar el valor del campo
   */
  onChange: PropTypes.func,
  /**
   * Colección de elementos del selector
   */
  elements: PropTypes.array.isRequired,
  /**
   * Indica si utiliza los estilos spaced
   */
  spaced: PropTypes.bool,
  /**
   * Indica si el campo está deshabilitado
   */
  disabled: PropTypes.bool,
  /**
   * Indica si se aplican los estilos de error al campo y al texto de ayuda
   */
  error: PropTypes.bool,
  /**
   * Indica si se aplican los estilos para placeholders multilínea en dispositivos móviles
   */
  marginDense: PropTypes.bool,
};

export default withStyles(styles)(Select);
